// FeedbackForm — modal feedback dialog + floating trigger button.
//
// Zero-backend pattern: on submit, the entry is saved to localStorage via
// UserFeedbackManager and a pre-filled mailto: URI is opened (if a feedback
// email is configured via VITE_FEEDBACK_EMAIL). Styled with Carry Roll's design tokens
// (CSS variables + .btn/.field-input component classes) so it matches the dark
// theme without relying on Tailwind utility colors.

import { useState, useEffect, useRef } from 'react';
import { MessageSquare, X } from 'lucide-react';
import {
  userFeedbackManager,
  buildMailtoUrl,
  type UserFeedback,
} from '../../utils/userFeedback';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'] | undefined;
  initialCategory?: string | undefined;
  initialError?: string | undefined; // pre-fill from an ErrorBoundary catch
}

export function FeedbackForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const feedbackTypes: { value: UserFeedback['type']; label: string }[] = [
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'general_feedback', label: 'General Feedback' },
    { value: 'usability_issue', label: 'Usability Issue' },
    { value: 'performance_issue', label: 'Performance Issue' },
  ];

  // Categories aligned with the Carry Roll ball-flight / gapping calculator domain.
  const categories = [
    'general',
    'ball_flight',
    'gapping',
    'optimize',
    'units',
    'ui_ux',
    'bug',
    'feature_request',
    'accessibility',
  ];

  const priorities: { value: UserFeedback['priority']; label: string }[] = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const contentRef = useRef<HTMLDivElement>(null);

  // Reset form state when modal opens.
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  // Focus the panel on open; restore focus to the previously active element on close.
  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    // Defer focus so the panel is mounted before we focus it.
    const id = window.setTimeout(() => contentRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(id);
      previousFocus?.focus?.();
    };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const prettyCategory = (c: string) =>
    c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--alert-overlay, rgba(0,0,0,0.65))', zIndex: 50 }}
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
      onClick={onClose}
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        className="panel w-full max-w-2xl overflow-y-auto"
        style={{
          maxHeight: '90vh',
          outline: 'none',
          margin: 0,
          // Give the panel a stronger shadow so it lifts above the backdrop.
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-bold)', margin: 0 }}>
            Send Feedback
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn"
            aria-label="Close"
            style={{ padding: 'var(--space-2)' }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {submitted ? (
          <div className="text-center" style={{ paddingBlock: 'var(--space-7)' }}>
            <p style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-medium)', marginBottom: 'var(--space-2)' }}>
              Thank you!
            </p>
            <p
              className="text-sm"
              style={{
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--space-4)',
              }}
            >
              Your mail client should have opened. Send the pre-filled email to complete.
            </p>
            {feedbackId && (
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
                Ref: {feedbackId}
              </p>
            )}
            <button type="button" onClick={onClose} className="btn btn-primary">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Type */}
            <div>
              <label htmlFor="fb-type" className="field-label">
                Type
              </label>
              <select
                id="fb-type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="field-input"
              >
                {feedbackTypes.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {ft.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="fb-category" className="field-label">
                Category
              </label>
              <select
                id="fb-category"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="field-input"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {prettyCategory(c)}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <span className="field-label">Priority</span>
              <div className="flex" style={{ gap: 'var(--space-2)' }}>
                {priorities.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => handleChange('priority', p.value)}
                    className="btn"
                    aria-pressed={formData.priority === p.value}
                    style={{
                      padding: 'var(--space-1) var(--space-3)',
                      fontSize: 'var(--fs-sm)',
                      fontWeight: 'var(--fw-medium)',
                      ...(formData.priority === p.value
                        ? {
                            backgroundColor: 'var(--color-accent)',
                            color: 'var(--color-bg)',
                            borderColor: 'var(--color-accent)',
                          }
                        : {}),
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="fb-title" className="field-label">
                Title *
              </label>
              <input
                id="fb-title"
                type="text"
                required
                maxLength={120}
                autoComplete="off"
                spellCheck
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Brief summary"
                className="field-input"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="fb-description" className="field-label">
                Description *
              </label>
              <textarea
                id="fb-description"
                rows={5}
                required
                maxLength={4000}
                spellCheck
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Please describe in detail..."
                className="field-input"
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {/* Actions */}
            <div
              className="flex items-center justify-end"
              style={{ gap: 'var(--space-3)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)' }}
            >
              <button type="button" onClick={onClose} className="btn">
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                style={{ opacity: isSubmitting || !formData.title.trim() || !formData.description.trim() ? 0.5 : 1 }}
              >
                {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- Floating trigger button ----------

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen()}
      aria-label="Send feedback"
      title="Send feedback"
      className="fab"
      style={{
        position: 'fixed',
        bottom: 'var(--space-4)',
        right: 'var(--space-4)',
        zIndex: 40,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '9999px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'var(--color-accent)',
        color: 'var(--color-bg)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      }}
    >
      <MessageSquare size={22} aria-hidden="true" />
    </button>
  );
}
